/**
 * Cube.js Client Configuration
 *
 * Creates a cubejsApi instance for analytics queries. Follows the same
 * authentication pattern as the Apollo client (Bearer token via getTokenWithRetry).
 *
 * Usage:
 *   import { cubejsApi } from "pages/SecurityCenter/cube/cubejsApi";
 *   // Provided globally via <CubeProvider cubejsApi={cubejsApi}> in Software.tsx
 *   // Use useCubeQuery() in components.
 */
import cubejs, { type CubeApi } from "@cubejs-client/core";
import { getRuntimeConfig, isFeatureEnabled } from "authentication/runtimeConfig";
import { getTokenWithRetry } from "utils/enhancedAuth";

/**
 * Cube.js API URL.
 *
 * In development: proxied through Vite dev server → /cubejs-api
 * In production:  injected via CUBEJS_API_URL env var at container startup (window.APP_CONFIG)
 *                 or falls back to VITE_CUBEJS_API_URL / relative path
 */
const getCubeJsApiUrl = (): string => {
  try {
    const config = getRuntimeConfig();
    if (config.api.cubejsApiUrl) return config.api.cubejsApiUrl;
  } catch {
    // fall through to default
  }
  return import.meta.env.VITE_CUBEJS_API_URL || `${window.location.origin}/cubejs-api/v1`;
};

const CUBEJS_API_URL = getCubeJsApiUrl();

/**
 * No-op Cube.js client used when the `cveManagement` feature is disabled.
 *
 * `load()` resolves with an empty `ResultSet`-like object so `useCubeQuery`
 * settles into a non-loading, no-data state instead of issuing HTTP requests
 * or hanging on a pending promise. The other methods reject (they are unused
 * by the current Security Center hooks) so misuse surfaces quickly.
 */
const createNoopCubejsApi = (): CubeApi => {
  const emptyResultSet = {
    rawData: () => [],
    tablePivot: () => [],
    chartPivot: () => [],
    seriesNames: () => [],
    series: () => [],
    annotation: () => ({ measures: {}, dimensions: {}, segments: {}, timeDimensions: {} }),
    query: () => ({}),
    decompose: () => [],
    drillDown: () => null,
    pivot: () => [],
    totalRow: () => ({}),
    categories: () => [],
    serializedResult: () => ({ loadResponse: { results: [] } }),
    serialize: () => ({ loadResponse: { results: [] } }),
  };
  const disabled = () => Promise.reject(new Error("Cube.js disabled: cveManagement feature is off"));
  const api = {
    load: () => Promise.resolve(emptyResultSet),
    sql: disabled,
    meta: disabled,
    dryRun: disabled,
    subscribe: () => Promise.resolve(emptyResultSet),
    request: disabled,
  };
  return api as unknown as CubeApi;
};

/**
 * cubejsApi instance authenticated with the user's existing Auth0 access token.
 *
 * Cube.js resolves the caller's tenant itself (via a custom checkAuth hook
 * server-side, see backend/src/cubejs/cube.js), so the frontend no longer
 * needs to mint a separate Cube-specific token — it just forwards the same
 * Auth0 access token used for GraphQL calls.
 *
 * If auth is disabled (dev mode), an empty string is returned.
 *
 * When the `cveManagement` feature is disabled, a no-op stub is exported so
 * the `<CubeProvider>` in Software.tsx stays valid but no HTTP traffic is generated.
 */
export const cubejsApi: CubeApi = isFeatureEnabled("cveManagement")
  ? cubejs(
      async () => {
        try {
          const authToken = await getTokenWithRetry(1);
          return authToken ?? "";
        } catch (e) {
          if (import.meta.env.DEV) console.warn("Cube.js: token retrieval failed, proceeding without auth", e);
          return "";
        }
      },
      { apiUrl: CUBEJS_API_URL }
    )
  : createNoopCubejsApi();

export default cubejsApi;
